import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, Building, ArrowRight, Shield, Plus, X, Landmark, ArrowLeft, ToggleLeft, ToggleRight, HelpCircle, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { getIn, useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, hasPermission } from '../../constants/permissions';

const getAssetUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path) || path.startsWith('/')) return path;
  const base = (config.S3_PUBLIC_BASE_URL || '').replace(/\/+$/, '');
  return base ? `${base}/${String(path).replace(/^\/+/, '')}` : '';
};

const ACADEMIC_CATEGORIES = ['K-12', 'Engineering & Tech', 'Arts & Science', 'Paramedical', 'Education'];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (value = '') => EMAIL_REGEX.test(String(value).trim().toLowerCase());
const isValidMobile = (value = '') => !value || String(value).replace(/\D/g, '').length === 10;
const slugifyText = (value = '') => String(value)
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const requiredMessage = (label) => `${label} is required`;
const trimValue = (value) => String(value || '').trim();
const getItemId = (item = {}) => item.id || item._id || '';

const buildClusterPath = (clusters = [], clusterId, fallbackCluster = null) => {
  const byId = new Map(clusters.map((cluster) => [getItemId(cluster), cluster]));
  const path = [];
  const visited = new Set();
  let current = byId.get(clusterId) || fallbackCluster;

  while (current && getItemId(current) && !visited.has(getItemId(current))) {
    const currentId = getItemId(current);
    visited.add(currentId);
    path.unshift(current);
    current = current.parentClusterId ? byId.get(current.parentClusterId) : null;
  }

  return path;
};

const institutionStepOneFields = [
  'collegeName',
  'slug',
  'logo',
  'legalBusinessName',
  'displayName',
  'businessType',
  'category',
  'contactName',
  'contactEmail',
  'contactPhone',
  'address.street1',
  'address.street2',
  'address.postalCode',
];

const institutionStepTwoFields = ['adminDetails.name', 'adminDetails.email', 'adminDetails.mobile'];
const institutionStepThreeFields = ['bankAccount.beneficiaryName', 'bankAccount.accountNumber', 'bankAccount.ifscCode'];

const buildTouched = (fields) => fields.reduce((acc, path) => {
  const parts = path.split('.');
  let cursor = acc;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) {
      cursor[part] = true;
      return;
    }
    cursor[part] = cursor[part] || {};
    cursor = cursor[part];
  });
  return acc;
}, {});

const hasErrorsForFields = (errors, fields) => fields.some(field => Boolean(getIn(errors, field)));

const validateInstitutionValues = (values, editingInstitution) => {
  const errors = {};
  if (!trimValue(values.collegeName)) errors.collegeName = requiredMessage('Institution name');
  if (!editingInstitution && !trimValue(values.slug)) errors.slug = requiredMessage('Slug');
  if (!editingInstitution && !values.logo) errors.logo = requiredMessage('Institution logo');
  if (!trimValue(values.legalBusinessName)) errors.legalBusinessName = requiredMessage('Legal business name');
  if (!trimValue(values.displayName)) errors.displayName = requiredMessage('Display name');
  if (!trimValue(values.businessType)) errors.businessType = requiredMessage('Business type');
  if (!trimValue(values.category)) errors.category = requiredMessage('Category');
  if (!trimValue(values.contactName)) errors.contactName = requiredMessage('Contact name');
  if (!trimValue(values.contactEmail)) {
    errors.contactEmail = requiredMessage('Contact email');
  } else if (!isValidEmail(values.contactEmail)) {
    errors.contactEmail = 'Enter a valid contact email';
  }
  if (!trimValue(values.contactPhone)) {
    errors.contactPhone = requiredMessage('Contact phone');
  } else if (!isValidMobile(values.contactPhone)) {
    errors.contactPhone = 'Enter a valid 10-digit contact phone';
  }
  if (!trimValue(values.address?.street1)) errors.address = { ...(errors.address || {}), street1: requiredMessage('Address line 1') };
  if (!trimValue(values.address?.street2)) errors.address = { ...(errors.address || {}), street2: requiredMessage('Address line 2') };
  if (!trimValue(values.address?.postalCode)) errors.address = { ...(errors.address || {}), postalCode: requiredMessage('Pincode') };

  const adminErrors = {};
  if (!trimValue(values.adminDetails?.name)) adminErrors.name = requiredMessage('Admin name');
  if (!trimValue(values.adminDetails?.email) && !trimValue(values.adminDetails?.mobile)) {
    adminErrors.email = 'Admin email or mobile is required';
    adminErrors.mobile = 'Admin email or mobile is required';
  } else {
    if (trimValue(values.adminDetails?.email) && !isValidEmail(values.adminDetails.email)) adminErrors.email = 'Enter a valid admin email';
    if (trimValue(values.adminDetails?.mobile) && !isValidMobile(values.adminDetails.mobile)) adminErrors.mobile = 'Enter a valid 10-digit admin mobile';
  }
  if (Object.keys(adminErrors).length) errors.adminDetails = adminErrors;

  const bankErrors = {};
  if (!trimValue(values.bankAccount?.beneficiaryName)) bankErrors.beneficiaryName = requiredMessage('Beneficiary name');
  if (!trimValue(values.bankAccount?.accountNumber)) bankErrors.accountNumber = requiredMessage('Account number');
  if (!trimValue(values.bankAccount?.ifscCode)) bankErrors.ifscCode = requiredMessage('IFSC code');
  if (Object.keys(bankErrors).length) errors.bankAccount = bankErrors;

  return errors;
};

const FieldLabel = ({ children, required = false, help = '' }) => (
  <span className="mb-1.5 flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-slate-500">
    {children}{required ? <span className="text-rose-500">*</span> : null}
    {help ? (
      <span className="relative inline-flex group">
        <HelpCircle size={14} className="text-slate-400 cursor-help" tabIndex={0} />
        <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden w-64 -translate-x-1/2 rounded-lg border border-slate-200 bg-slate-950 px-3 py-2 text-left text-xs font-semibold normal-case leading-relaxed tracking-normal text-white shadow-xl group-hover:block group-focus-within:block">
          {help}
        </span>
      </span>
    ) : null}
  </span>
);

const ErrorText = ({ children }) => children ? (
  <p className="mt-1.5 text-xs font-semibold text-rose-600">{children}</p>
) : null;

const inputClassName = (hasError, extra = '', disabled = false) => (
  `w-full px-4 py-3 border rounded-xl outline-none focus:ring-2 ${
    hasError ? 'border-rose-300 focus:ring-rose-200' : 'border-slate-200 focus:ring-indigo-500'
  } ${disabled ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : ''} ${extra}`
);

const TextField = ({ label, required = false, help = '', className = '', error = '', ...props }) => (
  <label className={`block ${className}`}>
    <FieldLabel required={required} help={help}>{label}</FieldLabel>
    <input
      {...props}
      required={required}
      className={inputClassName(Boolean(error), props.className || '', props.disabled)}
    />
    <ErrorText>{error}</ErrorText>
  </label>
);

export default function AdminColleges() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [accounts, setAccounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddCollege, setShowAddCollege] = useState(false);
  const [editingInstitution, setEditingInstitution] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const [adminDetailsSkipped, setAdminDetailsSkipped] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [checkingAdminEmail, setCheckingAdminEmail] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [clusters, setClusters] = useState([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 9;
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showCreateCluster, setShowCreateCluster] = useState(false);
  const [clusterStep, setClusterStep] = useState(1);
  const [selectedInstIds, setSelectedInstIds] = useState([]);
  const [clusterSearch, setClusterSearch] = useState('');
  const [existingClusterId, setExistingClusterId] = useState('');
  const [isNewCluster, setIsNewCluster] = useState(true);
  const [newClusterName, setNewClusterName] = useState('');
  const [newClusterDesc, setNewClusterDesc] = useState('');
  const [clusterAdminMode, setClusterAdminMode] = useState('create');
  const [clusterAdminName, setClusterAdminName] = useState('');
  const [clusterAdminEmail, setClusterAdminEmail] = useState('');
  const [clusterAdminMobile, setClusterAdminMobile] = useState('');
  const [clusterActiveTab, setClusterActiveTab] = useState('assign'); // 'assign' | 'view'
  const [expandedClusterId, setExpandedClusterId] = useState(null);
  const [expandedInstitutionClusters, setExpandedInstitutionClusters] = useState({});
  const [savingCluster, setSavingCluster] = useState(false);
  const [newCollege, setNewCollege] = useState({
    collegeName: '',
    slug: '',
    logo: null,
    legalBusinessName: '',
    displayName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    businessType: 'trust',
	    category: 'Engineering & Tech',
    subcategory: 'college',
    splitPaymentsEnabled: true,
    clusterId: '',
    pan: '',
    gst: '',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'IN',
    },
    bankAccount: {
      beneficiaryName: '',
      bankName: '',
      accountNumber: '',
      ifscCode: '',
      razorpayAccountId: '',
      razorpayProductId: '',
    },
    adminDetails: {
      name: '',
      email: '',
      mobile: '',
    },
  });
  const canCreateInstitutions = hasPermission(user, PERMISSIONS.INSTITUTIONS_CREATE);
  const canEditInstitutions = hasPermission(user, PERMISSIONS.INSTITUTIONS_EDIT);
  const canCreateClusters = user?.role === 'admin';
  const isSuperAdmin = user?.role === 'admin';
  const institutionFormik = useFormik({
    initialValues: newCollege,
    enableReinitialize: true,
    validate: (values) => validateInstitutionValues(values, editingInstitution),
    onSubmit: () => {},
  });
  const clusterFormik = useFormik({
    initialValues: {
      newClusterName,
      newClusterDesc,
      clusterAdminName,
      clusterAdminEmail,
      clusterAdminMobile,
      existingClusterId,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors = {};
      if (isNewCluster) {
        if (!trimValue(values.newClusterName)) errors.newClusterName = requiredMessage('Cluster name');
        if (user?.role === 'admin' && clusterAdminMode === 'create') {
          if (!trimValue(values.clusterAdminName)) errors.clusterAdminName = requiredMessage('Admin name');
          if (!trimValue(values.clusterAdminEmail)) {
            errors.clusterAdminEmail = requiredMessage('Admin email');
          } else if (!isValidEmail(values.clusterAdminEmail)) {
            errors.clusterAdminEmail = 'Enter a valid cluster admin email';
          }
          if (trimValue(values.clusterAdminMobile) && !isValidMobile(values.clusterAdminMobile)) {
            errors.clusterAdminMobile = 'Enter a valid 10-digit cluster admin mobile';
          }
        }
      } else if (!trimValue(values.existingClusterId)) {
        errors.existingClusterId = 'Select an existing cluster';
      }
      return errors;
    },
    onSubmit: () => {},
  });

  const getInstitutionError = (name) => getIn(institutionFormik.touched, name) && getIn(institutionFormik.errors, name);
  const getClusterError = (name) => getIn(clusterFormik.touched, name) && getIn(clusterFormik.errors, name);

  useEffect(() => {
    fetchCollegesData();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 2000);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchInstitutions = async () => {
    try {
      const params = { page, limit };
      if (debouncedSearch) params.q = debouncedSearch;
      const res = await axios.get('/api/admin/institutions', { params });
      setInstitutions(res.data?.items || []);
      setTotalPages(Math.ceil((res.data?.total || 0) / limit) || 1);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error('Failed to load institutions data');
    }
  };

  useEffect(() => {
    fetchInstitutions();
  }, [page, debouncedSearch]);

  const fetchCollegesData = async () => {
    try {
      const [accountsRes, clustersRes, coursesRes, programmesRes] = await Promise.all([
        axios.get('/api/admin/college-accounts'),
        axios.get('/api/admin/clusters'),
        isSuperAdmin ? Promise.resolve({ data: [] }) : axios.get('/api/admin/courses'),
        isSuperAdmin ? Promise.resolve({ data: [] }) : axios.get('/api/admin/programmes'),
      ]);
      setCourses(coursesRes.data || []);
      setProgrammes(programmesRes.data || []);
      setClusters(clustersRes.data || []);

      // Create quick lookup map for account mappings
      const accMap = {};
      accountsRes.data.forEach(acc => {
        accMap[acc.collegeName] = acc;
      });
      setAccounts(accMap);
    } catch (err) {
      toast.error('Failed to load configuration data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAccount = async (collegeName) => {
    const config = accounts[collegeName] || {};
    const payoutMode = config.payoutMode || 'razorpay_route';

    if (payoutMode === 'razorpay_route' && (!config.razorpayAccountId || !config.razorpayAccountId.trim())) {
      toast.error("Please enter a valid Razorpay Connected Account ID");
      return;
    }
    if (payoutMode === 'paytm_payout' && (!config.paytmWalletNumber || !config.paytmWalletNumber.trim())) {
      toast.error("Please enter a valid Paytm Wallet / Mobile Number");
      return;
    }
    if (payoutMode === 'direct_bank' && (!config.bankDetails?.accountNumber || !config.bankDetails?.ifscCode)) {
      toast.error("Account Number and IFSC Code are required for Direct Bank payout");
      return;
    }
    
    setSavingId(collegeName);
    try {
      const res = await axios.post('/api/admin/college-accounts', {
        collegeName,
        payoutMode,
        razorpayAccountId: config.razorpayAccountId?.trim(),
        paytmWalletNumber: config.paytmWalletNumber?.trim(),
        paytmMerchantId: config.paytmMerchantId?.trim(),
        bankDetails: config.bankDetails,
        active: config.active !== undefined ? config.active : true
      });
      
      setAccounts(prev => ({
        ...prev,
        [collegeName]: res.data.account
      }));
      toast.success(`Configured ${payoutMode === 'razorpay_route' ? 'Razorpay Route' : payoutMode === 'paytm_payout' ? 'Paytm Payout' : 'Direct Bank Payout'} for ${collegeName}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSavingId(null);
    }
  };

  const resetAddCollegeForm = () => {
    setFormStep(1);
    setAdminDetailsSkipped(false);
    setEditingInstitution(null);
    setSlugManuallyEdited(false);
    institutionFormik.resetForm({ values: {
      collegeName: '',
      slug: '',
      logo: null,
      legalBusinessName: '',
      displayName: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      businessType: 'trust',
      category: 'Engineering & Tech',
      subcategory: 'college',
      splitPaymentsEnabled: true,
      clusterId: '',
      pan: '',
      gst: '',
      address: {
        street1: '',
        street2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'IN',
      },
      bankAccount: {
        beneficiaryName: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        razorpayAccountId: '',
        razorpayProductId: '',
      },
      adminDetails: {
        name: '',
        email: '',
        mobile: '',
      },
    } });
    setNewCollege({
      collegeName: '',
      slug: '',
      logo: null,
      legalBusinessName: '',
      displayName: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      businessType: 'trust',
	      category: 'Engineering & Tech',
      subcategory: 'college',
      splitPaymentsEnabled: true,
      clusterId: '',
      pan: '',
      gst: '',
      address: {
        street1: '',
        street2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'IN',
      },
      bankAccount: {
        beneficiaryName: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        razorpayAccountId: '',
        razorpayProductId: '',
      },
      adminDetails: {
        name: '',
        email: '',
        mobile: '',
      },
    });
  };

  const updateNewCollege = (field, value) => {
    institutionFormik.setFieldTouched(field, true, false);
    institutionFormik.setFieldValue(field, value, true);
    setNewCollege(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'collegeName' && !slugManuallyEdited && !editingInstitution) {
        next.slug = slugifyText(value);
        institutionFormik.setFieldValue('slug', next.slug, true);
      }
      return next;
    });
  };

  const updateNewCollegeAddress = (field, value) => {
    institutionFormik.setFieldTouched(`address.${field}`, true, false);
    institutionFormik.setFieldValue(`address.${field}`, value, true);
    setNewCollege(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const lookupPincode = async (postalCode) => {
    const pin = String(postalCode || '').replace(/\D/g, '').slice(0, 6);
    updateNewCollegeAddress('postalCode', pin);
    if (pin.length !== 6) return;
    setPincodeLoading(true);
    try {
      const res = await axios.get(`/api/admin/pincode/${pin}`);
      setNewCollege(prev => ({
        ...prev,
        address: {
          ...prev.address,
          postalCode: pin,
          city: res.data?.city || prev.address.city,
          state: res.data?.state || prev.address.state,
          country: res.data?.country || 'IN',
        },
      }));
    } catch (err) {
      toast.error('Failed to fetch city and state from pincode');
    } finally {
      setPincodeLoading(false);
    }
  };

  const openEditInstitution = (e, institution) => {
    e.stopPropagation();
    if (!institution?._id) return;
    setEditingInstitution(institution);
    setFormStep(1);
    setSlugManuallyEdited(true);
    setNewCollege({
      collegeName: institution.collegeName || '',
      slug: institution.slug || '',
      logo: null,
      legalBusinessName: institution.legalBusinessName || institution.collegeName || '',
      displayName: institution.displayName || institution.collegeName || '',
      contactName: institution.contactName || '',
      contactEmail: institution.contactEmail || '',
      contactPhone: institution.contactPhone || '',
      businessType: institution.businessType || 'trust',
      category: institution.category || 'Engineering & Tech',
      subcategory: institution.subcategory || 'college',
      splitPaymentsEnabled: institution.splitPaymentsEnabled !== false,
      clusterId: institution.clusterId || institution.cluster?._id || institution.cluster?.id || '',
      pan: institution.pan || '',
      gst: institution.gst || '',
      address: {
        street1: institution.address?.street1 || '',
        street2: institution.address?.street2 || '',
        city: institution.address?.city || '',
        state: institution.address?.state || '',
        postalCode: institution.address?.postalCode || '',
        country: institution.address?.country || 'IN',
      },
      bankAccount: {
        beneficiaryName: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        razorpayAccountId: '',
        razorpayProductId: '',
      },
      adminDetails: {
        name: institution.institutionAdmin?.name || '',
        email: institution.institutionAdmin?.email || '',
        mobile: institution.institutionAdmin?.mobile || '',
      },
    });
    setShowAddCollege(true);
  };

  const updateNewCollegeBank = (field, value) => {
    institutionFormik.setFieldTouched(`bankAccount.${field}`, true, false);
    institutionFormik.setFieldValue(`bankAccount.${field}`, value, true);
    setNewCollege(prev => ({
      ...prev,
      bankAccount: { ...prev.bankAccount, [field]: value }
    }));
  };

  const updateNewCollegeAdmin = (field, value) => {
    setAdminDetailsSkipped(false);
    institutionFormik.setFieldTouched(`adminDetails.${field}`, true, false);
    institutionFormik.setFieldValue(`adminDetails.${field}`, value, true);
    setNewCollege(prev => ({
      ...prev,
      adminDetails: { ...prev.adminDetails, [field]: value }
    }));
  };

  const validateInstitutionStep = async (fields = institutionStepOneFields) => {
    institutionFormik.setTouched({ ...institutionFormik.touched, ...buildTouched(fields) }, true);
    const errors = await institutionFormik.validateForm();
    return !hasErrorsForFields(errors, fields);
  };

  const handleNextAddCollegeStep = async () => {
    if (formStep === 1 && (await validateInstitutionStep(institutionStepOneFields))) {
      setFormStep(2);
      return;
    }
    if (formStep === 2) {
      if (!(await validateInstitutionStep(institutionStepTwoFields))) return;
      const admin = newCollege.adminDetails;
      if (admin.email.trim()) {
        setCheckingAdminEmail(true);
        try {
          const res = await axios.get('/api/admin/admin-users/check-email', {
            params: { email: admin.email.trim().toLowerCase() },
          });
          if (res.data?.exists) {
            toast.error('This email id is already registered');
            return;
          }
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to check admin email');
          return;
        } finally {
          setCheckingAdminEmail(false);
        }
      }
      setAdminDetailsSkipped(false);
      setFormStep(3);
    }
  };

  const handleSkipAdminDetails = () => {
    setAdminDetailsSkipped(true);
    setFormStep(3);
  };

  const handleOnboardCollege = async (skipBankDetails = false) => {
    const allFields = [
      ...institutionStepOneFields,
      ...(adminDetailsSkipped ? [] : institutionStepTwoFields),
      ...(skipBankDetails ? [] : institutionStepThreeFields),
    ];
    if (!(await validateInstitutionStep(allFields))) return;
    const admin = newCollege.adminDetails;
    setOnboarding(true);
    try {
      const fd = new FormData();
      fd.append('institution', JSON.stringify({
          collegeName: newCollege.collegeName.trim(),
          slug: newCollege.slug.trim(),
          legalBusinessName: newCollege.legalBusinessName.trim(),
          displayName: newCollege.displayName.trim() || newCollege.collegeName.trim(),
          contactName: newCollege.contactName.trim(),
          contactEmail: newCollege.contactEmail.trim(),
          contactPhone: newCollege.contactPhone.trim(),
          businessType: newCollege.businessType,
          category: newCollege.category,
          subcategory: newCollege.subcategory,
          splitPaymentsEnabled: skipBankDetails ? false : newCollege.splitPaymentsEnabled,
          pan: newCollege.pan.trim(),
          gst: newCollege.gst.trim(),
          address: newCollege.address,
          clusterId: newCollege.clusterId || null,
        }));
      if (!skipBankDetails) fd.append('bankAccount', JSON.stringify({
          beneficiaryName: newCollege.bankAccount.beneficiaryName.trim(),
          bankName: newCollege.bankAccount.bankName.trim(),
          accountNumber: newCollege.bankAccount.accountNumber.trim(),
          ifscCode: newCollege.bankAccount.ifscCode.trim(),
          razorpayAccountId: newCollege.bankAccount.razorpayAccountId.trim(),
          razorpayProductId: newCollege.bankAccount.razorpayProductId.trim(),
        }));
      if (!adminDetailsSkipped) fd.append('adminDetails', JSON.stringify({
	          name: admin.name.trim(),
	          email: admin.email.trim(),
	          mobile: admin.mobile.trim(),
	        }));
      if (newCollege.logo) fd.append('logo', newCollege.logo);

      const res = await axios.post('/api/admin/institutions/onboard', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const account = res.data.collegeAccount;
      if (account?.collegeName) {
        setAccounts(prev => ({ ...prev, [account.collegeName]: account }));
      }
      toast.success(skipBankDetails
        ? 'Institution added. Bank details can be added later.'
        : 'Institution added and payment account linked successfully');
      setShowAddCollege(false);
      resetAddCollegeForm();
      fetchCollegesData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to onboard institution');
    } finally {
      setOnboarding(false);
    }
  };

  const handleUpdateInstitution = async () => {
    if (!editingInstitution?._id || !(await validateInstitutionStep(institutionStepOneFields))) return;
    setOnboarding(true);
    try {
      const fd = new FormData();
      fd.append('institution', JSON.stringify({
        collegeName: newCollege.collegeName.trim(),
        legalBusinessName: newCollege.legalBusinessName.trim(),
        displayName: newCollege.displayName.trim(),
        contactName: newCollege.contactName.trim(),
        contactEmail: newCollege.contactEmail.trim(),
        contactPhone: newCollege.contactPhone.trim(),
        businessType: newCollege.businessType,
        category: newCollege.category,
        subcategory: newCollege.subcategory,
        splitPaymentsEnabled: newCollege.splitPaymentsEnabled,
        pan: newCollege.pan.trim(),
        gst: newCollege.gst.trim(),
        address: newCollege.address,
        clusterId: newCollege.clusterId || null,
      }));
      if (newCollege.logo) fd.append('logo', newCollege.logo);
      await axios.patch(`/api/admin/institutions/${editingInstitution._id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Institution details updated');
      setShowAddCollege(false);
      resetAddCollegeForm();
      await Promise.all([fetchInstitutions(), fetchCollegesData()]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update institution');
    } finally {
      setOnboarding(false);
    }
  };

  const handleToggleSplitPayments = async (e, institution) => {
    e.stopPropagation();
    if (!institution?._id) return;
    const nextValue = !institution.splitPaymentsEnabled;
    setInstitutions(prev => prev.map(item => (
      item._id === institution._id ? { ...item, splitPaymentsEnabled: nextValue } : item
    )));
    try {
      const res = await axios.patch(`/api/admin/institutions/${institution._id}/split-payments`, {
        splitPaymentsEnabled: nextValue,
      });
      setInstitutions(prev => prev.map(item => (
        item._id === institution._id ? { ...item, ...res.data.institution } : item
      )));
      toast.success(nextValue ? 'Split payments enabled' : 'Split payments disabled');
    } catch (err) {
      setInstitutions(prev => prev.map(item => (
        item._id === institution._id ? { ...item, splitPaymentsEnabled: !nextValue } : item
      )));
      toast.error(err.response?.data?.message || 'Failed to update split setting');
    }
  };


  const collegesMap = {};
  
  // Initialize only for institutions on the current paginated page
  institutions.forEach(institution => {
    const key = institution.displayName || institution.collegeName;
    if (key) {
      collegesMap[key] = [];
    }
  });

  // Populate courses only for those institutions
  courses.forEach(c => {
    const key = c.institution?.displayName || c.institutionName || c.collegeName;
    if (collegesMap[key] !== undefined) {
      collegesMap[key].push(c);
    }
  });

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 ${
            page === i
              ? 'z-10 bg-indigo-600 text-white focus-visible:outline-focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
              : 'text-slate-900 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:outline-offset-0'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>Onboarded Institutions</h1>
          {/* <p className="text-slate-500 mt-1">Configure automated split payments and settlement profiles for each institution.</p> */}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
	          {user?.role === 'admin' && canCreateInstitutions && (
              <button
                onClick={() => setShowAddCollege(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white font-bold text-sm rounded-xl hover:bg-slate-800 transition-colors"
              >
                <Plus size={17} />
                Add Institution
              </button>
            )}
	            {canCreateClusters && (
              <button
                onClick={() => {
	                  setClusterStep(1);
	                  setSelectedInstIds([]);
                    clusterFormik.resetForm();
	                  setNewClusterName('');
                  setNewClusterDesc('');
                  setClusterAdminMode('create');
                  setClusterAdminName('');
                  setClusterAdminEmail('');
                  setClusterAdminMobile('');
                  setClusterActiveTab('assign');
                  setExpandedClusterId(null);
                  setIsNewCluster(true);
                  setExistingClusterId('');
                  setShowCreateCluster(true);
                }}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors"
              >
                <Plus size={17} />
                Create Cluster
              </button>
            )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Search Institution or Course..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none w-64 md:w-80"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.keys(collegesMap).length === 0 ? (
          <div className="bg-white p-8 rounded-2xl text-center text-slate-500 shadow-sm border border-slate-200">No institutions found.</div>
        ) : (
		          Object.keys(collegesMap).map(collegeName => {
		            const institution = institutions.find(item => item.displayName === collegeName || item.collegeName === collegeName);
	            const logoUrl = getAssetUrl(institution?.logoPath);
	            const routeBase = user?.role === 'cluster_admin' ? '/cluster-admin' : '/admin';
              const institutionId = institution?._id || institution?.id || collegeName;
              const directClusters = Array.isArray(institution?.clusters) ? institution.clusters : [];
              const fallbackClusterPath = buildClusterPath(clusters, institution?.clusterId || institution?.cluster?.id || institution?.cluster?._id, institution?.cluster);
              const clusterItems = directClusters.length ? directClusters : fallbackClusterPath;
              const showAllClusters = Boolean(expandedInstitutionClusters[institutionId]);
              const visibleClusterItems = showAllClusters ? clusterItems : clusterItems.slice(0, 2);
              const hiddenClusterCount = Math.max(clusterItems.length - visibleClusterItems.length, 0);
	            return (
	            <div 
	              key={collegeName} 
              onClick={() => navigate(`${routeBase}/colleges/${encodeURIComponent(collegeName)}`)}
              className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden transition-all cursor-pointer hover:-translate-y-1 hover:shadow-lg hover:border-indigo-300 group flex flex-col h-full"
            >
              <div className="p-6 flex-1 flex flex-col justify-between">
	                <div>
                  <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-4 overflow-hidden border border-slate-100 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                    {logoUrl ? <img src={logoUrl} alt={collegeName} className="w-full h-full object-contain p-1 bg-white" /> : <Building size={24} />}
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="text-xl font-extrabold text-slate-800 leading-tight mb-2 group-hover:text-indigo-600 transition-colors">{institution?.displayName || collegeName}</h2>
                    {canEditInstitutions && (
                      <button
                        type="button"
                        onClick={(e) => openEditInstitution(e, institution)}
                        className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-600"
                        aria-label={`Edit ${institution?.displayName || collegeName}`}
                      >
                        <Pencil size={15} />
                      </button>
                    )}
                  </div>
	                  {clusterItems.length > 0 && (
	                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        {visibleClusterItems.map((cluster) => (
                          <span
                            key={getItemId(cluster)}
                            className="max-w-full truncate rounded-lg bg-indigo-50 px-2 py-1 text-xs font-bold text-indigo-700"
                            title={cluster.name}
                          >
                            {cluster.name}
                          </span>
                        ))}
                        {hiddenClusterCount > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedInstitutionClusters(prev => ({ ...prev, [institutionId]: true }));
                            }}
                            className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 hover:bg-slate-200"
                          >
                            +{hiddenClusterCount} more
                          </button>
                        )}
                        {showAllClusters && clusterItems.length > 2 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedInstitutionClusters(prev => ({ ...prev, [institutionId]: false }));
                            }}
                            className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 hover:bg-slate-200"
                          >
                            Show less
                          </button>
                        )}
	                    </div>
	                  )}
                  {!isSuperAdmin && (
                    <>
                      <p className="text-sm font-medium text-slate-500 bg-slate-50 w-max px-3 py-1 rounded-lg mb-2">
                        {collegesMap[collegeName].length} Courses Available
                      </p>
                      <p className="text-xs text-slate-400">
                        {programmes.filter(p =>
                          p.institution?.collegeName === collegeName
                          || p.institution?.displayName === collegeName
                          || p.institutionName === collegeName
                          || p.institutionId === institution?._id
                        ).length} Programmes
                      </p>
                    </>
                  )}
	                    {/* {canEditInstitutions && <button
	                      type="button"
                      onClick={(e) => handleToggleSplitPayments(e, institution)}
                      className={`mt-4 inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-extrabold transition-colors ${
                        institution?.splitPaymentsEnabled !== false
                          ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                          : 'border-slate-200 bg-slate-100 text-slate-500'
                      }`}
                    >
	                      {institution?.splitPaymentsEnabled !== false ? <ToggleRight size={17} /> : <ToggleLeft size={17} />}
	                      Split {institution?.splitPaymentsEnabled !== false ? 'On' : 'Off'}
	                    </button>} */}
	                </div>

                {/* Multi-Provider Payout Configuration */}
           
              </div>
              <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex items-center justify-between text-sm font-bold text-slate-600 group-hover:bg-indigo-50 group-hover:text-indigo-700 transition-colors">
                {isSuperAdmin ? 'View Details' : 'View All Courses'}
                <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          )})
        )}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6 mt-4 rounded-2xl shadow-sm mb-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="relative ml-3 inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing <span className="font-bold">{(page - 1) * limit + 1}</span> to{' '}
                <span className="font-bold">{Math.min(page * limit, total)}</span> of{' '}
                <span className="font-bold">{total}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-xl shadow-sm bg-white border border-slate-200 overflow-hidden" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-r border-slate-200"
                >
                  &larr;
                </button>
                {renderPageNumbers()}
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-l border-slate-200"
                >
                  &rarr;
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {showAddCollege && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border border-slate-200">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
	                <h2 className="text-xl font-extrabold text-slate-900">{editingInstitution ? 'Edit Institution' : 'Add Institution'}</h2>
                <p className="text-sm text-slate-500">
	                  {editingInstitution ? 'Update institution profile details' : formStep === 1 ? 'Enter institution details' : formStep === 2 ? 'Create institution admin' : 'Enter settlement bank details'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAddCollege(false);
                  resetAddCollegeForm();
                }}
                className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
                aria-label="Close add institution"
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-6 pt-5">
              <div className="grid grid-cols-3 gap-3">
                <div className={`h-2 rounded-full ${formStep >= 1 ? 'bg-indigo-600' : 'bg-slate-200'}`} />
                <div className={`h-2 rounded-full ${formStep >= 2 ? 'bg-indigo-600' : 'bg-slate-200'}`} />
                <div className={`h-2 rounded-full ${formStep >= 3 ? 'bg-indigo-600' : 'bg-slate-200'}`} />
              </div>
            </div>

            <div className="p-6">
              {formStep === 1 ? (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <Building size={18} className="text-indigo-600" />
                    Institution Details
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
		                    <TextField label="Institution Name" required value={newCollege.collegeName} error={getInstitutionError('collegeName')} onChange={(e) => updateNewCollege('collegeName', e.target.value)} />
	                    <TextField
	                      label="Slug"
	                      required={!editingInstitution}
	                      help="The slug is used in institution URLs, for example sns-college-of-technology."
		                      value={newCollege.slug}
		                      disabled={Boolean(editingInstitution)}
                          error={getInstitutionError('slug')}
	                      onChange={(e) => {
	                        setSlugManuallyEdited(true);
	                        updateNewCollege('slug', slugifyText(e.target.value));
	                      }}
	                    />
		                    <TextField label="Legal Business Name" required value={newCollege.legalBusinessName} error={getInstitutionError('legalBusinessName')} onChange={(e) => updateNewCollege('legalBusinessName', e.target.value)} />
	                    <label className="block">
	                      <FieldLabel required={!editingInstitution}>Institution Logo</FieldLabel>
		                      <span className={inputClassName(Boolean(getInstitutionError('logo')), 'block cursor-pointer bg-white text-slate-500')}>
		                        <span className="block text-sm font-semibold text-slate-700">{newCollege.logo ? newCollege.logo.name : editingInstitution ? 'Upload New Logo' : 'Upload Institution Logo'}</span>
		                      </span>
		                      <input type="file" accept="image/*" className="hidden" onChange={(e) => updateNewCollege('logo', e.target.files?.[0] || null)} />
                          <ErrorText>{getInstitutionError('logo')}</ErrorText>
		                    </label>
		                    <TextField label="Display Name" required value={newCollege.displayName} error={getInstitutionError('displayName')} onChange={(e) => updateNewCollege('displayName', e.target.value)} />
		                    <label className="block">
		                      <FieldLabel required>Business Type</FieldLabel>
			                      <select className={inputClassName(Boolean(getInstitutionError('businessType')), 'bg-white')} value={newCollege.businessType} onChange={(e) => updateNewCollege('businessType', e.target.value)}>
	                      <option value="trust">Trust</option>
	                      <option value="society">Society</option>
	                      <option value="partnership">Partnership</option>
	                      <option value="private_limited">Private Limited</option>
	                      <option value="public_limited">Public Limited</option>
	                      <option value="individual">Individual</option>
			                    </select>
                          <ErrorText>{getInstitutionError('businessType')}</ErrorText>
			                    </label>
			                    <label className="block">
			                      <FieldLabel required>Category</FieldLabel>
			                      <select className={inputClassName(Boolean(getInstitutionError('category')), 'bg-white')} value={newCollege.category} onChange={(e) => updateNewCollege('category', e.target.value)}>
		                      <option value="">Select Category *</option>
		                      {ACADEMIC_CATEGORIES.map(category => (
		                        <option key={category} value={category}>{category}</option>
		                      ))}
			                    </select>
                          <ErrorText>{getInstitutionError('category')}</ErrorText>
			                    </label>
		                    <label className="block">
		                      <FieldLabel>Cluster</FieldLabel>
		                      <select className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-white" value={newCollege.clusterId || ''} onChange={(e) => updateNewCollege('clusterId', e.target.value)}>
	                      <option value="">No Cluster (Independent)</option>
	                      {clusters.map(cluster => (
	                        <option key={cluster.id || cluster._id} value={cluster.id || cluster._id}>{cluster.name}</option>
	                      ))}
	                    </select>
	                    </label>
                    <label className="md:col-span-2 flex items-center justify-between gap-4 px-4 py-3 border border-slate-200 rounded-xl bg-slate-50">
                      <span>
                        <span className="block text-sm font-bold text-slate-700">Enable Split Payments</span>
                        <span className="block text-xs text-slate-500">When off, full payments stay in the primary Razorpay account.</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => updateNewCollege('splitPaymentsEnabled', !newCollege.splitPaymentsEnabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${newCollege.splitPaymentsEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${newCollege.splitPaymentsEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </label>
		                    <TextField label="Contact Name" required value={newCollege.contactName} error={getInstitutionError('contactName')} onChange={(e) => updateNewCollege('contactName', e.target.value)} />
		                    <TextField label="Contact Email" required type="email" value={newCollege.contactEmail} error={getInstitutionError('contactEmail')} onChange={(e) => updateNewCollege('contactEmail', e.target.value)} />
		                    <TextField label="Contact Phone" required value={newCollege.contactPhone} error={getInstitutionError('contactPhone')} onChange={(e) => updateNewCollege('contactPhone', e.target.value)} />
	                    <TextField label="PAN" value={newCollege.pan} onChange={(e) => updateNewCollege('pan', e.target.value.toUpperCase())} />
	                    <TextField label="GST" value={newCollege.gst} onChange={(e) => updateNewCollege('gst', e.target.value.toUpperCase())} />
		                    <TextField label="Address Line 1" required value={newCollege.address.street1} error={getInstitutionError('address.street1')} onChange={(e) => updateNewCollegeAddress('street1', e.target.value)} />
		                    <TextField label="Address Line 2" required value={newCollege.address.street2} error={getInstitutionError('address.street2')} onChange={(e) => updateNewCollegeAddress('street2', e.target.value)} />
		                    <TextField label={pincodeLoading ? 'Pincode - fetching...' : 'Pincode'} required value={newCollege.address.postalCode} error={getInstitutionError('address.postalCode')} onChange={(e) => lookupPincode(e.target.value)} />
	                    <TextField label="City" value={newCollege.address.city} onChange={(e) => updateNewCollegeAddress('city', e.target.value)} disabled />
	                    <TextField label="State" value={newCollege.address.state} disabled />
                  </div>
                </div>
              ) : formStep === 2 ? (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <Shield size={18} className="text-indigo-600" />
                    Institution Admin
	                  </div>
	                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
	                    <TextField label="Admin Name" required value={newCollege.adminDetails.name} error={getInstitutionError('adminDetails.name')} onChange={(e) => updateNewCollegeAdmin('name', e.target.value)} />
	                    <TextField label="Admin Email" type="email" value={newCollege.adminDetails.email} error={getInstitutionError('adminDetails.email')} onChange={(e) => updateNewCollegeAdmin('email', e.target.value)} />
	                    <TextField label="Admin Mobile" value={newCollege.adminDetails.mobile} error={getInstitutionError('adminDetails.mobile')} onChange={(e) => updateNewCollegeAdmin('mobile', e.target.value.replace(/\D/g, '').slice(-10))} />
	                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
                    The admin will be linked to this institution and can sign in with OTP using the email or mobile entered here.
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                    <Landmark size={18} className="text-indigo-600" />
                    Bank Details
	                  </div>
	                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
		                    <TextField label="Beneficiary Name" required value={newCollege.bankAccount.beneficiaryName} error={getInstitutionError('bankAccount.beneficiaryName')} onChange={(e) => updateNewCollegeBank('beneficiaryName', e.target.value)} />
		                    <TextField label="Bank Name" value={newCollege.bankAccount.bankName} onChange={(e) => updateNewCollegeBank('bankName', e.target.value)} />
		                    <TextField label="Account Number" required value={newCollege.bankAccount.accountNumber} error={getInstitutionError('bankAccount.accountNumber')} onChange={(e) => updateNewCollegeBank('accountNumber', e.target.value)} />
		                    <TextField label="IFSC Code" required value={newCollege.bankAccount.ifscCode} error={getInstitutionError('bankAccount.ifscCode')} onChange={(e) => updateNewCollegeBank('ifscCode', e.target.value.toUpperCase())} />
		                  </div>
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-sm text-indigo-900">
                    Submitting will create a Razorpay linked account, request the Route product, update settlement bank details, then save the institution and bank account records.
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-3">
              {formStep > 1 ? (
                <button
                  onClick={() => setFormStep(prev => Math.max(1, prev - 1))}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50"
                >
                  <ArrowLeft size={17} />
                  Back
                </button>
              ) : <span />}

              {editingInstitution ? (
                <button
                  onClick={handleUpdateInstitution}
                  disabled={onboarding}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:bg-indigo-400"
                >
                  <Shield size={17} />
                  {onboarding ? 'Saving...' : 'Save Institution'}
                </button>
		              ) : formStep < 3 ? (
                    <div className="flex items-center gap-3">
                      {formStep === 2 && (
                        <button
                          onClick={handleSkipAdminDetails}
                          disabled={checkingAdminEmail}
                          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50"
                        >
                          Skip Admin Details
                        </button>
                      )}
		                  <button
		                    onClick={handleNextAddCollegeStep}
		                    disabled={checkingAdminEmail}
		                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed"
		                  >
		                    {checkingAdminEmail ? 'Checking...' : 'Continue'}
		                    <ArrowRight size={17} />
		                  </button>
                    </div>
	              ) : (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleOnboardCollege(true)}
                      disabled={onboarding}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50"
                    >
                      Skip Bank Details
                    </button>
	                  <button
	                    onClick={() => handleOnboardCollege(false)}
	                    disabled={onboarding}
	                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:bg-indigo-400"
	                  >
	                    <Shield size={17} />
	                    {onboarding ? 'Creating Razorpay Account...' : 'Create Linked Account'}
	                  </button>
                  </div>
	              )}
            </div>
          </div>
        </div>
      )}

      {showCreateCluster && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border border-slate-200 flex flex-col">
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900">Institution Clusters</h2>
                <div className="flex gap-4 mt-2">
                  <button 
                    onClick={() => setClusterActiveTab('assign')}
                    className={`text-sm font-bold pb-1 border-b-2 transition-colors ${clusterActiveTab === 'assign' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500'}`}
                  >
                    Select & Create 
                  </button>
                  <button 
                    onClick={() => setClusterActiveTab('view')}
                    className={`text-sm font-bold pb-1 border-b-2 transition-colors ${clusterActiveTab === 'view' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500'}`}
                  >
                    View Clusters
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowCreateCluster(false)}
                className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto">
              {clusterActiveTab === 'assign' ? (
                clusterStep === 1 ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input 
                        type="text" 
                        placeholder="Search institution to add..." 
                        value={clusterSearch}
                        onChange={(e) => setClusterSearch(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none w-full"
                      />
                    </div>

                    <div className="border border-slate-200 rounded-xl overflow-hidden max-h-60 overflow-y-auto divide-y divide-slate-100">
                      {institutions
                        .filter(inst => (inst.displayName || inst.collegeName || '').toLowerCase().includes(clusterSearch.toLowerCase()))
                        .map(inst => {
                          const id = inst.id || inst._id;
                          const isChecked = selectedInstIds.includes(id);
                          return (
                            <label key={id} className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer text-sm font-semibold text-slate-700">
                              <input 
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {
                                  setSelectedInstIds(prev => 
                                    prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
                                  );
                                }}
                                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                              />
                              <span>{inst.displayName || inst.collegeName}</span>
                            </label>
                          );
                        })}
                    </div>
                    <div className="text-xs font-semibold text-slate-500">
                      {selectedInstIds.length} institutions selected
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                        <input 
                          type="radio" 
                          name="clusterType" 
                          checked={isNewCluster} 
                          onChange={() => setIsNewCluster(true)} 
                          className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        Create a New Cluster
                      </label>
                      <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                        <input 
                          type="radio" 
                          name="clusterType" 
                          checked={!isNewCluster} 
                          onChange={() => setIsNewCluster(false)} 
                          className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        Add to Existing Cluster
                      </label>
                    </div>

                    {isNewCluster ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Cluster Details</label>
                          <input 
                            type="text" 
                            placeholder="Cluster Name *" 
                            value={newClusterName}
	                            onChange={(e) => {
                                clusterFormik.setFieldTouched('newClusterName', true, false);
                                clusterFormik.setFieldValue('newClusterName', e.target.value, true);
                                setNewClusterName(e.target.value);
                              }}
	                            className={inputClassName(Boolean(getClusterError('newClusterName')), 'mb-3')}
	                          />
                            <ErrorText>{getClusterError('newClusterName')}</ErrorText>
	                          <textarea 
                            placeholder="Cluster Details / Description" 
                            value={newClusterDesc}
	                            onChange={(e) => {
                                clusterFormik.setFieldValue('newClusterDesc', e.target.value, false);
                                setNewClusterDesc(e.target.value);
                              }}
	                            className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 h-20 resize-none"
                          />
                        </div>
	                        {user?.role === 'admin' && (
	                        <div className="border-t border-slate-100 pt-4">
	                          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Cluster Admin</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                              <label className={`flex items-start gap-2 rounded-xl border p-3 cursor-pointer ${clusterAdminMode === 'create' ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white'}`}>
                                <input
                                  type="radio"
                                  name="clusterAdminMode"
                                  checked={clusterAdminMode === 'create'}
                                  onChange={() => setClusterAdminMode('create')}
                                  className="mt-1"
                                />
                                <span>
                                  <span className="block text-sm font-bold text-slate-800">Create admin</span>
                                  <span className="block text-xs text-slate-500 mt-0.5">Add a cluster admin now.</span>
                                </span>
                              </label>
                              <label className={`flex items-start gap-2 rounded-xl border p-3 cursor-pointer ${clusterAdminMode === 'skip' ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white'}`}>
                                <input
                                  type="radio"
                                  name="clusterAdminMode"
                                  checked={clusterAdminMode === 'skip'}
                                  onChange={() => setClusterAdminMode('skip')}
                                  className="mt-1"
                                />
                                <span>
                                  <span className="block text-sm font-bold text-slate-800">Skip for now</span>
                                  <span className="block text-xs text-slate-500 mt-0.5">Create the cluster without an admin.</span>
                                </span>
                              </label>
                            </div>
	                          {clusterAdminMode === 'create' && <div className="space-y-3">
	                            <input 
	                              type="text" 
	                              placeholder="Admin Name *" 
                              value={clusterAdminName}
	                              onChange={(e) => {
                                  clusterFormik.setFieldTouched('clusterAdminName', true, false);
                                  clusterFormik.setFieldValue('clusterAdminName', e.target.value, true);
                                  setClusterAdminName(e.target.value);
                                }}
	                              className={inputClassName(Boolean(getClusterError('clusterAdminName')))}
	                            />
                              <ErrorText>{getClusterError('clusterAdminName')}</ErrorText>
	                            <input 
                              type="email" 
                              placeholder="Admin Email *" 
                              value={clusterAdminEmail}
	                              onChange={(e) => {
                                  clusterFormik.setFieldTouched('clusterAdminEmail', true, false);
                                  clusterFormik.setFieldValue('clusterAdminEmail', e.target.value, true);
                                  setClusterAdminEmail(e.target.value);
                                }}
	                              className={inputClassName(Boolean(getClusterError('clusterAdminEmail')))}
	                            />
                              <ErrorText>{getClusterError('clusterAdminEmail')}</ErrorText>
	                            <input 
                              type="text" 
                              placeholder="Admin Mobile" 
                              value={clusterAdminMobile}
	                              onChange={(e) => {
                                  clusterFormik.setFieldTouched('clusterAdminMobile', true, false);
                                  clusterFormik.setFieldValue('clusterAdminMobile', e.target.value, true);
                                  setClusterAdminMobile(e.target.value);
                                }}
	                              className={inputClassName(Boolean(getClusterError('clusterAdminMobile')))}
	                            />
	                              <ErrorText>{getClusterError('clusterAdminMobile')}</ErrorText>
	                          </div>}
	                        </div>
                          )}
	                      </div>
                    ) : (
                      <div className="space-y-3">
                        <select 
                          value={existingClusterId}
	                          onChange={(e) => {
                              clusterFormik.setFieldTouched('existingClusterId', true, false);
                              clusterFormik.setFieldValue('existingClusterId', e.target.value, true);
                              setExistingClusterId(e.target.value);
                            }}
	                          className={inputClassName(Boolean(getClusterError('existingClusterId')), 'bg-white')}
                        >
                          <option value="">-- Select Existing Cluster --</option>
                          {clusters.map(c => (
                            <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>
                          ))}
	                        </select>
                          <ErrorText>{getClusterError('existingClusterId')}</ErrorText>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="space-y-4">
                  {clusters.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-8">No clusters created yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {clusters.map(cluster => {
                        const isExpanded = expandedClusterId === cluster.id;
                        return (
                          <div key={cluster.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                            <button
                              onClick={() => setExpandedClusterId(isExpanded ? null : cluster.id)}
                              className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between text-left border-b border-slate-100"
                            >
                              <div>
                                <h3 className="font-bold text-slate-800">{cluster.name}</h3>
                                {cluster.description && <p className="text-xs text-slate-500 mt-0.5">{cluster.description}</p>}
                              </div>
                              <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">
                                {cluster.institutions?.length || 0} Colleges
                              </span>
                            </button>
                            {isExpanded && (
                              <div className="p-4 divide-y divide-slate-100 bg-white">
                                {cluster.institutions?.length === 0 ? (
                                  <p className="text-xs text-slate-400">No institutions assigned to this cluster.</p>
                                ) : (
                                  cluster.institutions.map(inst => (
                                    <div key={inst.id} className="py-2 text-sm text-slate-700 flex items-center gap-2 first:pt-0 last:pb-0">
                                      <Building size={14} className="text-slate-400" />
                                      <span>{inst.displayName || inst.collegeName}</span>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-3">
              {clusterActiveTab === 'view' ? (
                <div className="w-full flex justify-end">
                  <button
                    onClick={() => setShowCreateCluster(false)}
                    className="px-5 py-2.5 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  {clusterStep === 2 ? (
                    <button
                      onClick={() => setClusterStep(1)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50"
                    >
                      <ArrowLeft size={17} />
                      Back
                    </button>
                  ) : <span />}

                  {clusterStep === 1 ? (
                    <button
                      onClick={() => {
                        if (selectedInstIds.length === 0) {
                          toast.error('Please select at least one institution');
                          return;
                        }
                        setClusterStep(2);
                      }}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700"
                    >
                      Continue
                      <ArrowRight size={17} />
                    </button>
                  ) : (
	                    <button
	                      onClick={async () => {
	                        let targetClusterId = existingClusterId;
                          const clusterFields = isNewCluster
                            ? [
                                'newClusterName',
                                ...(user?.role === 'admin' && clusterAdminMode === 'create'
                                  ? ['clusterAdminName', 'clusterAdminEmail', 'clusterAdminMobile']
                                  : [])
                              ]
                            : ['existingClusterId'];
                          clusterFormik.setTouched({ ...clusterFormik.touched, ...buildTouched(clusterFields) }, true);
                          const clusterErrors = await clusterFormik.validateForm();
                          if (hasErrorsForFields(clusterErrors, clusterFields)) return;
	                        if (isNewCluster) {
	                          setSavingCluster(true);
                          try {
                            const createClusterPayload = {
	                              name: newClusterName.trim(),
	                              description: newClusterDesc.trim(),
                              ...(user?.role === 'admin' && clusterAdminMode === 'create' ? {
                                clusterAdmin: {
	                                name: clusterAdminName.trim(),
	                                email: clusterAdminEmail.trim().toLowerCase(),
	                                mobile: clusterAdminMobile.trim() || null
                                }
                              } : { skipClusterAdmin: true })
                            };
                            let res;
                            try {
                              res = await axios.post('/api/admin/clusters', createClusterPayload);
                            } catch (err) {
                              if (err.response?.status === 409 && err.response?.data?.code === 'USER_EXISTS_CAN_PROMOTE') {
                                const confirmed = window.confirm(`${clusterAdminEmail.trim().toLowerCase()} is already a user. Promote this user to cluster admin for the new cluster?`);
                                if (!confirmed) {
                                  setSavingCluster(false);
                                  return;
                                }
                                res = await axios.post('/api/admin/clusters', {
                                  ...createClusterPayload,
                                  confirmPromoteExistingUser: true,
                                });
                              } else {
                                throw err;
                              }
                            }
	                            targetClusterId = res.data.cluster?.id || res.data.cluster?._id;
	                          } catch (err) {
                            toast.error(err.response?.data?.message || 'Failed to create cluster');
                            setSavingCluster(false);
                            return;
                          }
	                        }

                        // Now assign institutions to this cluster
                        setSavingCluster(true);
                        try {
                          await axios.post('/api/admin/clusters/assign', {
                            clusterId: targetClusterId,
                            institutionIds: selectedInstIds
                          });
                          toast.success('Institutions added to cluster successfully');
                          setShowCreateCluster(false);
                          fetchCollegesData();
                        } catch (err) {
                          toast.error(err.response?.data?.message || 'Failed to assign institutions');
                        } finally {
                          setSavingCluster(false);
                        }
                      }}
                      disabled={savingCluster}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:bg-indigo-400"
                    >
                      {savingCluster ? 'Saving...' : 'Save & Assign'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
